import { useQuery } from '@tanstack/react-query';
import {
  type Core_PartySource,
  EmulateHeaders,
  GeneralGroupType,
  type UseQueryReturnType,
  gqlClient,
  graphql,
  queryClient,
} from '@tyro/api';
import { sortByDisplayName } from '@tyro/core';
import { groupsKeys } from './keys';

const customGroupsList = graphql(/* GraphQL */ `
  query customGroupsList($filter: GeneralGroupFilter!) {
    generalGroups(filter: $filter) {
      partyId
      name
      avatarUrl
      studentMembers {
        memberCount
      }
      staffMembers {
        memberCount
      }
      contactMembers {
        memberCount
      }
    }
  }
`);

const customGroupById = graphql(/* GraphQL */ `
  query customGroupById($filter: GeneralGroupFilter!) {
    generalGroups(filter: $filter) {
      partyId
      name
      avatarUrl
      students {
        partyId
        classGroup {
          name
        }
        person {
          partyId
          firstName
          lastName
          avatarUrl
          type
          title {
            id
            name
            nameTextId
          }
        }
        extensions {
          priority
          aen
          medical
        }
      }
    }
  }
`);

const customGroupsQuery = (
  excludeGroups?: [Core_PartySource.SchoolActivity, Core_PartySource.Timetable],
  academicNamespaceId?: number | undefined,
) => ({
  queryKey: groupsKeys.custom.groups(excludeGroups, academicNamespaceId),
  queryFn: async () =>
    gqlClient.request(
      customGroupsList,
      {
        filter: {
          groupTypes: [GeneralGroupType.CustomGroup],
          excludeSources: excludeGroups ? excludeGroups : [],
        },
      },
      typeof academicNamespaceId === 'number'
        ? {
            [EmulateHeaders.ACADEMIC_NAMESPACE_ID]:
              academicNamespaceId.toString(),
          }
        : undefined,
    ),
});

export function getCustomGroups(
  excludeGroups?: [Core_PartySource.SchoolActivity, Core_PartySource.Timetable],
  academicNamespaceId?: number | undefined,
) {
  return queryClient.fetchQuery(
    customGroupsQuery(excludeGroups, academicNamespaceId),
  );
}

export function useCustomGroups(
  excludeGroups?: [Core_PartySource.SchoolActivity, Core_PartySource.Timetable],
  academicNamespaceId?: number | undefined,
) {
  return useQuery({
    ...customGroupsQuery(excludeGroups, academicNamespaceId),
    select: ({ generalGroups }) =>
      generalGroups?.filter(({ name }) => !['Duty', 'On Call'].includes(name)), // Filtered as are system groups that are controlled elsewhere
  });
}

const customGroupsByIdQuery = (id: number | undefined) => ({
  queryKey: groupsKeys.custom.details(id),
  queryFn: async () => {
    const { generalGroups } = await gqlClient.request(customGroupById, {
      filter: {
        partyIds: [id ?? 0],
      },
    });

    const [group] = generalGroups || [];

    return {
      ...group,
      students: (group.students || []).sort((studentA, studentB) =>
        sortByDisplayName(studentA.person, studentB.person),
      ),
    };
  },
});

export function getCustomGroupById(id: number | undefined) {
  return queryClient.fetchQuery(customGroupsByIdQuery(id));
}

export function useCustomGroupById(id: number | undefined) {
  return useQuery({
    ...customGroupsByIdQuery(id),
    select: (group) => group,
  });
}

export type ReturnTypeFromUseCustomGroups = UseQueryReturnType<
  typeof useCustomGroups
>[number];
